import { convertToInlineCss } from '@atlaskit/editor-common/css-helper';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { token } from '@atlaskit/tokens';

import type { AiAutocompletePluginState } from '../types/types';

const GHOST_INLINE_CLASS = 'heading-autocomplete-ghost-inline';
const GHOST_HINT_CLASS = 'heading-autocomplete-ghost-text-hint';

const ghostInlineStyle = convertToInlineCss({
	color: token('color.text.subtlest'),
	opacity: '0.95',
	pointerEvents: 'auto',
	userSelect: 'none',
	whiteSpace: 'pre-wrap',
	cursor: 'pointer',
});

const ghostHintStyle = convertToInlineCss({
	backgroundColor: token('color.background.accent.gray.subtlest'),
	borderRadius: token('radius.small'),
	padding: token('space.050'),
	font: token('font.body.small'),
	marginLeft: token('space.050'),
	verticalAlign: '3px',
});

const ghostHintTabStyle = convertToInlineCss({
	border: `1px solid ${token('color.border.bold')}`,
	borderRadius: token('radius.small'),
	padding: `0 ${token('space.025')}`,
});
/**
 * Builds the appropriate ProseMirror DecorationSet for the current plugin state.
 *
 * • heading-idle trigger → widget decoration after the heading node (ghost paragraph)
 * • manual trigger       → inline widget decoration at the cursor (ghost inline text)
 *
 * All decorations are aria-hidden and non-editable — they never touch the doc.
 */
export function buildSuggestionDecoration(
	state: EditorState,
	pluginState: Extract<AiAutocompletePluginState, { status: 'suggestion' }>,
): DecorationSet {
	return buildInlineGhostDecoration(state, pluginState.cursorPos, pluginState.suggestion);
}

/**
 * Inline ghost text at the cursor position (manual Cmd+Shift+Space trigger).
 * Accepted with Tab or click.
 */
function buildInlineGhostDecoration(
	state: EditorState,
	cursorPos: number,
	suggestion: string,
): DecorationSet {
	const widget = Decoration.widget(
		cursorPos,
		() => {
			const span = document.createElement('span');
			span.className = GHOST_INLINE_CLASS;
			span.setAttribute('aria-hidden', 'true');
			span.setAttribute('contenteditable', 'false');
			span.setAttribute('data-testid', 'heading-autocomplete-ghost-inline');
			span.setAttribute('style', ghostInlineStyle);

			const text = document.createTextNode(suggestion);
			span.appendChild(text);

			const hint = document.createElement('span');
			hint.className = GHOST_HINT_CLASS;
			hint.setAttribute('style', ghostHintStyle);
			span.appendChild(hint);

			const tab = document.createElement('span');
			tab.className = GHOST_HINT_CLASS;
			tab.setAttribute('style', ghostHintTabStyle);
			tab.textContent = 'Tab';
			hint.appendChild(tab);

			const toAcceptText = document.createTextNode(' or click to accept');
			hint.appendChild(toAcceptText);

			return span;
		},
		{ side: 1, key: 'heading-autocomplete-ghost-inline' },
	);

	return DecorationSet.create(state.doc, [widget]);
}
